/**
 * Author the rule — v10.4 V104-G-10 (ADR-0076 §6–§8, D-104-15).
 *
 * Opened from "Author the rule" on a Needs authoring card. The editor builds
 * the trigger from the frozen rule fields (all / any of conditions), lists the
 * duties, and quotes the stored law text for the trigger and for every duty.
 * Quotes are checked as they are typed with the same rule the server applies
 * (ADR-0076 §6); the server checks again and files nothing unless every quote
 * matches. Filing creates an ordinary pending proposal in the Editorial queue.
 *
 * Design pass (ux-adversary, runs/v104-g10-authoring-form): no fixed footer
 * (nothing can cover the focused field); one shared status region; the error
 * summary takes focus on a failed submit, the success panel on success; the
 * law text is a side pane on wide screens and a sheet on narrow ones;
 * selecting law text offers "Use as quote"; the draft is kept on this device.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Drawer,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Radio,
  Select,
  Spinner,
  Textarea,
} from '@aigovops/design-system';
import { CANONICAL_ROLE_LABEL, normalizeForGrounding, type JurisdictionTerm, type RuleAuthoringReason } from '@aigovops/core';
import { ApiError, actOnAuthoringItem, fileAuthoredRule, getAuthoringContext, listAuthoringItems, type AuthoredRuleRequest, type AuthoringContextResponse } from '../lib/api';
import { useAuth } from '../lib/auth';
import { AUTHOR_RULE_DRAFT_PREFIX } from '../lib/draft-keys';
import { queryKeys } from '../lib/query-keys';
import { jurisdictionName } from '../lib/ai-program-model';
import { useNarrow } from '../lib/use-narrow';
import { LawTextPane } from '../components/LawTextPane';
import { TermProposalForm } from '../components/TermProposalForm';
import { NewDutyFields } from '../components/NewDutyFields';
import {
  AMOUNT_LABEL,
  DEADLINE_LABEL,
  ELSE_VALUE,
  FIELD_CATALOG,
  OPERATOR_LABEL,
  TIER_LABEL,
  adoptTerms,
  dutyLabelFields,
  roleChoices,
  tierChoices,
  blankCase,
  blankCondition,
  blankDuty,
  blankFact,
  blankNewDuty,
  closestDuty,
  newDutyProblems,
  plainNewDutyIssue,
  proposing,
  caseMessage,
  casesMissing,
  casesToFixtures,
  checkCase,
  nextFactKey,
  checkQuote,
  conditionComplete,
  dutyFromDraft,
  fieldSpec,
  fromPredicate,
  locateQuote,
  operatorsFor,
  plainFixtureIssue,
  quoteMessage,
  startingCases,
  toDeadlineRule,
  toDutyId,
  toPredicate,
  type CaseDraft,
  type CaseResult,
  type Combinator,
  type Condition,
  type DeadlineKind,
  type DutyDraft,
  type Fact,
  type LabelChoice,
  type QuoteStatus,
} from '../lib/rule-authoring';

const REASON_LABEL: Record<RuleAuthoringReason, string> = {
  provider_error: 'Drafting failed',
  thin_source: 'No usable law text',
  already_covered: 'Already covered',
  vocabulary_gap: 'Needs a new rule field',
  multi_rule_law: 'Needs several rules',
  ungrounded: 'Draft not backed by the text',
};
const MAX_QUOTES = 5;
const MAX_DUTIES = 20;
const MIN_LAW_TEXT = 2000;

interface FormState {
  combinator: Combinator;
  conditions: Condition[];
  triggerQuotes: string[];
  duties: DutyDraft[];
  effectiveDate: string;
  /** V104-G-13: null = the starting cases built from the conditions; an array once the editor has changed them. */
  cases: CaseDraft[] | null;
}
type Problem = { target: string; text: string };

// Security review info item: the draft key carries the user, and drafts are cleared on sign-out (lib/auth).
const storageKey = (draftKey: string) => `${AUTHOR_RULE_DRAFT_PREFIX}${draftKey}`;
function loadSaved(itemId: string): FormState | null {
  try {
    const raw = window.localStorage.getItem(storageKey(itemId));
    if (!raw) return null;
    const s = JSON.parse(raw) as FormState;
    if (!(Array.isArray(s.conditions) && Array.isArray(s.duties) && Array.isArray(s.triggerQuotes))) return null;
    // Drafts saved before per-duty dates and test cases lack these; fill them in.
    return {
      ...s,
      duties: s.duties.map((d) => ({ ...d, startsOn: d.startsOn ?? '', roleTerms: Array.isArray(d.roleTerms) ? d.roleTerms : [], tierTerm: d.tierTerm ?? '' })),
      cases: Array.isArray(s.cases) ? s.cases : null,
    };
  } catch {
    // Storage blocked or a malformed save: start from the law instead.
    return null;
  }
}
function save(itemId: string, s: FormState | null) {
  try {
    if (s) window.localStorage.setItem(storageKey(itemId), JSON.stringify(s));
    else window.localStorage.removeItem(storageKey(itemId));
  } catch {
    // Storage blocked: the draft just isn't kept between visits.
  }
}

function initialState(ctx: AuthoringContextResponse): FormState {
  const fallbackCitation = ctx.law.bill_id ?? ctx.law.short_title ?? '';
  const draft = (ctx.draft ?? {}) as { predicate?: unknown; predicate_excerpts?: unknown; obligations?: unknown };
  const parsed = fromPredicate(draft.predicate);
  const quotes = Array.isArray(draft.predicate_excerpts) ? draft.predicate_excerpts.filter((q): q is string => typeof q === 'string').slice(0, MAX_QUOTES) : [];
  const terms = ctx.terms ?? [];
  const duties = Array.isArray(draft.obligations)
    ? draft.obligations.slice(0, MAX_DUTIES).map((o) => adoptTerms(dutyFromDraft(o, fallbackCitation), terms))
    : [];
  return {
    combinator: parsed?.combinator ?? 'all',
    conditions: parsed?.conditions ?? [blankCondition()],
    triggerQuotes: quotes.length ? quotes : [''],
    duties: duties.length ? duties : [blankDuty(fallbackCitation)],
    effectiveDate: ctx.law.effective_date ?? '',
    cases: null,
  };
}

function fmtDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function AuthorRule() {
  const { slug = '', itemId = '' } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = !user || user.role === 'admin' || user.role === 'editor';
  const queryClient = useQueryClient();
  const ctxQuery = useQuery({ queryKey: ['authoring-items', 'context', itemId], queryFn: () => getAuthoringContext(itemId), retry: false });
  const backHref = `/w/${slug}/app/queue?lane=authoring`;
  const back = () => navigate(backHref);
  const [done, setDone] = useState<{ itemId: string; law: string; count: number } | null>(null);
  const doneRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (done) doneRef.current?.focus();
  }, [done]);

  if (done && done.itemId === itemId) {
    return <Done law={done.law} count={done.count} slug={slug} itemId={itemId} onBack={back} headingRef={doneRef} navigate={navigate} />;
  }

  if (ctxQuery.isLoading) return <Spinner label="Loading the law" />;
  if (ctxQuery.error || !ctxQuery.data) {
    const gone = ctxQuery.error instanceof ApiError && ctxQuery.error.status === 404;
    return (
      <EmptyState
        title={gone ? 'This law is no longer waiting for a rule' : 'Couldn’t load this law'}
        action={gone ? <Button onClick={back}>Back to Needs authoring</Button> : <Button onClick={() => void ctxQuery.refetch()}>Try again</Button>}
      />
    );
  }
  const ctx = ctxQuery.data;
  if (ctx.item.state === 'resolved') {
    return <EmptyState title="This law already has its rule" action={<Button onClick={back}>Back to Needs authoring</Button>} />;
  }
  if (!canWrite) {
    return <EmptyState title="Only editors can author rules" action={<Button onClick={back}>Back to Needs authoring</Button>} />;
  }
  return (
    <AuthorRuleForm
      key={itemId}
      ctx={ctx}
      itemId={itemId}
      draftKey={`${user?.id ?? 'anon'}:${itemId}`}
      slug={slug}
      onBack={back}
      onDone={(count) => setDone({ itemId, law: ctx.law.short_title || ctx.law.title, count })}
      refresh={async () => {
        await queryClient.invalidateQueries({ queryKey: ['authoring-items'] });
        await queryClient.invalidateQueries({ queryKey: ['queue'] });
      }}
    />
  );
}

function AuthorRuleForm({ ctx, itemId, draftKey, onBack, onDone, refresh }: {
  ctx: AuthoringContextResponse;
  itemId: string;
  /** `<userId>:<itemId>`: the browser draft belongs to one user. */
  draftKey: string;
  slug: string;
  onBack: () => void;
  onDone: (count: number) => void;
  refresh: () => Promise<void>;
}) {
  const narrow = useNarrow();
  const law = ctx.law.short_title || ctx.law.title;
  const item = ctx.item;
  const multi = item.reason_code === 'multi_rule_law';
  const text = ctx.statute_text?.text ?? null;
  const normalized = useMemo(() => (text ? normalizeForGrounding(text) : ''), [text]);

  const saved = useMemo(() => loadSaved(draftKey), [draftKey]);
  const [form, setForm] = useState<FormState>(() => saved ?? initialState(ctx));
  const [restored, setRestored] = useState(!!saved);
  const [message, setMessage] = useState<{ n: number; text: string } | null>(null);
  const [problems, setProblems] = useState<Problem[]>([]);
  const [serverIssues, setServerIssues] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [lastQuoteField, setLastQuoteField] = useState<string | null>(null);
  // Focus moves after the render that creates (or keeps) the target, not on a timer.
  const [focusTarget, setFocusTarget] = useState<{ id: string } | null>(null);
  const focusOn = useCallback((id: string) => setFocusTarget({ id }), []);
  useEffect(() => {
    if (!focusTarget) return;
    document.getElementById(focusTarget.id)?.focus();
    setFocusTarget(null);
  }, [focusTarget]);
  const [focusRange, setFocusRange] = useState<{ start: number; end: number; label: string; n: number } | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [lawText, setLawText] = useState('');
  const [lawTextError, setLawTextError] = useState<string | null>(null);
  const summaryRef = useRef<HTMLDivElement>(null);
  const formHeadingRef = useRef<HTMLHeadingElement>(null);
  const filed = ctx.filed;
  const terms = ctx.terms ?? [];
  // V112-5-5 (ADR-0116): a short name is a known duty, one waiting for review as a new duty, or new.
  const knownDuties = ctx.duties?.known ?? [];
  const dutySets = useMemo(() => ({ known: new Set(knownDuties), pending: new Set(ctx.duties?.pending ?? []) }), [knownDuties, ctx.duties?.pending]);
  const dutyState = useCallback(
    (id: string): 'known' | 'pending' | 'new' => (dutySets.known.has(id) ? 'known' : dutySets.pending.has(id) ? 'pending' : 'new'),
    [dutySets]
  );
  // Round 1 #6 / round 2: every other card using a short name points at the (first) card proposing it.
  const proposedIn = (i: number): { index: number; key: number } | null => {
    const id = toDutyId(form.duties[i]?.duty ?? '');
    if (id.length < 2) return null;
    const j = form.duties.findIndex((d) => toDutyId(d.duty) === id && proposing(d));
    return j < 0 || j === i ? null : { index: j, key: form.duties[j]!.key };
  };
  // When the law gains words (one was just added), plain picks take the law's word where exactly one maps.
  const termsKey = terms.map((t) => `${t.kind}:${t.term_key}:${t.status}`).join('|');
  useEffect(() => {
    if (!terms.length) return;
    setForm((f) => ({ ...f, duties: f.duties.map((d) => adoptTerms(d, terms)) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termsKey]);
  const usesPendingTerm = form.duties.some(
    (d) => d.roleTerms.some((k) => terms.find((t) => t.kind === 'role' && t.term_key === k)?.status === 'pending') ||
      (!!d.tierTerm && terms.find((t) => t.kind === 'tier' && t.term_key === d.tierTerm)?.status === 'pending')
  );

  const announce = useCallback((t: string) => setMessage((m) => ({ n: (m?.n ?? 0) + 1, text: t })), []);

  // Keep the draft on this device (a per-viewer convenience; never the record).
  const filedRef = useRef(false);
  useEffect(() => {
    if (filedRef.current) return;
    const t = setTimeout(() => save(draftKey, form), 400);
    return () => clearTimeout(t);
  }, [form, draftKey]);

  // A client-side navigation lands here: put focus on the page heading (WCAG 2.4.3).
  useEffect(() => {
    const h = document.querySelector<HTMLElement>('#author-rule-h1');
    if (h) {
      h.setAttribute('tabindex', '-1');
      h.focus();
    }
  }, []);

  const status = (q: string): QuoteStatus => (text ? checkQuote(normalized, q) : 'empty');
  const failing = [
    ...form.triggerQuotes.map((q, i) => ({ id: `tq-${i}`, s: status(q) })),
    ...form.duties.map((d, i) => ({ id: `dq-${d.key}`, s: status(d.quote), i })),
  ].filter((x) => x.s === 'too_short' || x.s === 'not_in_text');

  // V104-G-13: the cases shown are the editor's once they change any; until then, the starting set built from the conditions.
  const predicate = useMemo(() => toPredicate(form.combinator, form.conditions), [form.combinator, form.conditions]);
  const cases = useMemo(() => form.cases ?? startingCases(predicate), [form.cases, predicate]);
  const caseResults = useMemo(() => cases.map((c) => checkCase(predicate, c)), [cases, predicate]);
  const badCases = cases.map((c, i) => ({ id: `cn-${c.key}`, i, r: caseResults[i]! })).filter((x) => x.r.state !== 'agrees');
  // One step back after a case is removed or the set is replaced (a per-session convenience).
  const [undoCases, setUndoCases] = useState<{ cases: CaseDraft[] | null; text: string } | null>(null);
  const startingNow = useMemo(() => startingCases(predicate), [predicate]);
  const differsFromStarting = form.cases !== null && JSON.stringify(casesToFixtures(form.cases)) !== JSON.stringify(casesToFixtures(startingNow));
  const missingKinds = predicate ? casesMissing(cases) : [];
  const updateCases = (fn: (current: CaseDraft[]) => CaseDraft[]) =>
    setForm((f) => ({ ...f, cases: fn(f.cases ?? startingCases(toPredicate(f.combinator, f.conditions))) }));

  const set = (patch: Partial<FormState>) => setForm((f) => ({ ...f, ...patch }));
  const setDuty = (key: number, patch: Partial<DutyDraft>) =>
    setForm((f) => ({ ...f, duties: f.duties.map((d) => (d.key === key ? { ...d, ...patch } : d)) }));

  const quoteTargets = [
    ...form.triggerQuotes.map((_, i) => ({ id: `tq-${i}`, label: form.triggerQuotes.length > 1 ? `Who it applies to, quote ${i + 1}` : 'Who it applies to' })),
    ...form.duties.map((d, i) => ({ id: `dq-${d.key}`, label: `Duty ${i + 1}${d.title ? `: ${d.title.slice(0, 40)}` : ''}` })),
  ];

  const find = (label: string, q: string) => {
    if (!text) return;
    const s = status(q);
    if (s !== 'found') {
      if (narrow) setSheetOpen(true);
      const msg = s === 'empty' ? `${label}: add a quote first, or select it in the law text.` : `${label}: ${quoteMessage(s, q)}`;
      // The sheet makes the page inert while it opens; let the message land after it.
      setTimeout(() => announce(msg), narrow ? 300 : 0);
      return;
    }
    const at = locateQuote(text, q);
    if (!at) {
      announce(`${label}: found, but it couldn’t be highlighted.`);
      return;
    }
    setFocusRange((r) => ({ ...at, label, n: (r?.n ?? 0) + 1 }));
    if (narrow) setSheetOpen(true);
  };

  const useSelection = (targetId: string, value: string) => {
    if (targetId.startsWith('tq-')) {
      const i = Number(targetId.slice(3));
      setForm((f) => ({ ...f, triggerQuotes: f.triggerQuotes.map((q, j) => (j === i ? value : q)) }));
    } else {
      const key = Number(targetId.slice(3));
      setDuty(key, { quote: value });
    }
    setSheetOpen(false);
    const label = quoteTargets.find((t) => t.id === targetId)?.label ?? 'Quote';
    announce(`${label}: quote filled from the law text.`);
    focusOn(targetId);
  };

  function validate(): Problem[] {
    const out: Problem[] = [];
    if (!toPredicate(form.combinator, form.conditions)) out.push({ target: 'cond-0-value', text: 'Who it applies to: add at least one complete condition.' });
    form.conditions.forEach((c, i) => {
      if (!conditionComplete(c)) out.push({ target: `cond-${i}-value`, text: `Condition ${i + 1}: choose a value.` });
    });
    const filledQuotes = form.triggerQuotes.filter((q) => q.trim());
    if (filledQuotes.length === 0) out.push({ target: 'tq-0', text: 'Who it applies to: quote the passage that says who the law covers.' });
    form.triggerQuotes.forEach((q, i) => {
      const s = status(q);
      if (s === 'too_short' || s === 'not_in_text') out.push({ target: `tq-${i}`, text: `Who it applies to, quote ${i + 1}: ${quoteMessage(s, q)}` });
    });
    form.duties.forEach((d, i) => {
      const n = `Duty ${i + 1}`;
      if (d.title.trim().length < 3) out.push({ target: `dt-${d.key}`, text: `${n}: add a title.` });
      if (toDutyId(d.duty).length < 2) out.push({ target: `dn-${d.key}`, text: `${n}: add a short name.` });
      else if (dutyState(toDutyId(d.duty)) === 'new' && !proposedIn(i)) {
        // V112-5-5: a duty outside the vocabulary is filed only as a proposed new duty (once, by its first card).
        if (!proposing(d)) out.push({ target: `dnp-${d.key}`, text: `${n}: “${d.duty.trim()}” isn’t a known duty. Propose it, or use a known duty.` });
        else newDutyProblems(d.newDuty).forEach((p) => out.push({ target: `${p.field}-${d.key}`, text: `${n}: ${p.text}` }));
      }
      if (!d.citation.trim()) out.push({ target: `dc-${d.key}`, text: `${n}: add the section it comes from.` });
      if (!toDeadlineRule(d)) out.push({ target: `da-${d.key}`, text: `${n}: the deadline needs a whole number.` });
      const s = status(d.quote);
      if (s === 'empty') out.push({ target: `dq-${d.key}`, text: `${n}: quote the passage that imposes this duty.` });
      else if (s !== 'found') out.push({ target: `dq-${d.key}`, text: `${n}: ${quoteMessage(s, d.quote)}` });
    });
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.effectiveDate)) out.push({ target: 'starts-on', text: 'Starts on: pick a date.' });
    form.duties.forEach((d, i) => {
      if (!d.startsOn) return;
      if (!/^\d{4}-\d{2}-\d{2}$/.test(d.startsOn)) out.push({ target: `de-${d.key}`, text: `Duty ${i + 1}: pick a valid start date, or clear it.` });
      else if (/^\d{4}-\d{2}-\d{2}$/.test(form.effectiveDate) && d.startsOn < form.effectiveDate) {
        out.push({ target: `de-${d.key}`, text: `Duty ${i + 1}: starts before the rule does (${fmtDate(form.effectiveDate)}). The rule’s start date is the earliest, so move it earlier or this duty later.` });
      }
    });
    cases.forEach((c, i) => {
      const r = caseResults[i]!;
      if (r.state !== 'agrees') out.push({ target: `cn-${c.key}`, text: `Test case ${i + 1}${c.label.trim() ? ` (${c.label.trim()})` : ''}: ${caseMessage(r, c)}` });
    });
    if (predicate) casesMissing(cases).forEach((text) => out.push({ target: 'add-case', text }));
    return out;
  }

  function showProblems(list: Problem[], issues: string[] = []) {
    setProblems(list);
    setServerIssues(issues);
    focusOn('problems-summary');
  }

  async function submit(finish: boolean) {
    if (busy) return;
    const list = validate();
    if (list.length) {
      showProblems(list);
      return;
    }
    setBusy(true);
    setProblems([]);
    setServerIssues([]);
    // V112-5-5: each proposed new duty once (two duty cards may share a short name).
    const seen = new Set<string>();
    const newDuties = form.duties.flatMap((d) => {
      const id = toDutyId(d.duty);
      if (!proposing(d) || dutyState(id) !== 'new' || seen.has(id)) return [];
      seen.add(id);
      const nd = d.newDuty;
      return [{
        value: id,
        label: nd.label.trim(),
        definition: nd.definition.trim(),
        control: nd.controlMode === 'existing' ? { control_id: nd.controlId } : { new: { title: nd.controlTitle.trim(), description: nd.controlDescription.trim() } },
      }];
    });
    try {
      const res = await fileAuthoredRule(itemId, {
        ...(newDuties.length ? { new_duties: newDuties } : {}),
        predicates: toPredicate(form.combinator, form.conditions)!,
        predicate_excerpts: form.triggerQuotes.filter((q) => q.trim()),
        obligations: form.duties.map((d) => ({
          title: d.title.trim(),
          duty: toDutyId(d.duty),
          citation: d.citation.trim(),
          deadline_rule: toDeadlineRule(d)!,
          // V104-G-12: the jurisdiction's words plus the fixed values they count as (never typed by the editor).
          ...dutyLabelFields(d, terms),
          ...(d.startsOn ? { effective_date: d.startsOn } : {}),
          source_excerpt: d.quote,
        })),
        effective_date: form.effectiveDate,
        test_fixtures: casesToFixtures(cases) as unknown as NonNullable<AuthoredRuleRequest["test_fixtures"]>,
        ...(multi ? { finish } : {}),
      });
      if (res.resolved) {
        filedRef.current = true;
        save(draftKey, null);
        onDone(res.filed.length);
        void refresh();
        return;
      }
      await refresh();
      // Multi-rule law: keep who it applies to; start the next rule's duties.
      const next: FormState = { ...form, duties: [blankDuty(ctx.law.bill_id ?? '')], cases: form.cases };
      setForm(next);
      save(draftKey, next);
      announce(`Rule ${res.filed.length} filed for review. Write rule ${res.filed.length + 1}.`);
      focusOn('rule-form-h');
    } catch (err) {
      const body = (err instanceof ApiError ? err.body : null) as {
        error?: string;
        detail?: string;
        errors?: string[];
        unmatched?: Array<{ part: string; problem: string }>;
        issues?: string[];
      } | null;
      if (body?.error === 'ungrounded' && body.unmatched) {
        showProblems(
          body.unmatched.map((u) => {
            const m = /^obligation:(\d+)$/.exec(u.part);
            const d = m ? form.duties[Number(m[1])] : undefined;
            const where = d ? `Duty ${Number(m![1]) + 1}` : 'Who it applies to';
            const what = u.problem === 'too_short' ? 'quote is too short' : u.problem === 'missing_excerpt' ? 'quote is missing' : 'quote is not in the law text';
            return { target: d ? `dq-${d.key}` : 'tq-0', text: `${where}: ${what}.` };
          })
        );
      } else if (body?.error === 'no_statute_text') {
        showProblems([{ target: 'law-text-h', text: 'There is no stored law text to quote from. Add it below.' }]);
      } else if (body?.error) {
        // Round 1 #7: a refusal about a new duty is pointed at its field, in the form's words.
        const cards = form.duties.map((d) => ({ key: d.key, id: toDutyId(d.duty) }));
        const all = [...(body.errors ?? []), ...(body.issues ?? [])];
        const targeted = all.map((e) => plainNewDutyIssue(e, cards));
        const issues = all.filter((_, i) => !targeted[i]).map((e) => plainFixtureIssue(e) ?? e);
        const list = targeted.filter((t): t is { target: string; text: string } => !!t);
        // Round 2: the vocabulary or the controls changed under the editor (a term approved, a control removed); re-read them.
        if (list.length) await refresh();
        showProblems(list, issues.length || list.length ? [...new Set(issues)] : [body.detail ?? 'The rule wasn’t filed.']);
      } else {
        showProblems([], [err instanceof Error ? err.message : 'The rule wasn’t filed. Try again.']);
      }
    } finally {
      setBusy(false);
    }
  }

  async function finishWithoutAnother() {
    if (busy || filed.length === 0) return;
    setBusy(true);
    try {
      await actOnAuthoringItem(itemId, 'author_rule', {
        linked_rule_ids: filed.map((f) => f.change_id),
        note: `Authored in the app: ${filed.length} rule${filed.length === 1 ? '' : 's'} filed for review.`,
      });
      filedRef.current = true;
      save(draftKey, null);
      onDone(filed.length);
      void refresh();
    } catch (err) {
      showProblems([], [err instanceof Error ? err.message : 'That didn’t save.']);
    } finally {
      setBusy(false);
    }
  }

  async function requestField(concept: string) {
    if (busy) return;
    setBusy(true);
    try {
      await actOnAuthoringItem(itemId, 'request_vocabulary', { concept });
      await refresh();
      announce(`Rule field requested: ${concept.replace(/_/g, ' ')}. You can still write the rule with the fields below.`);
    } catch (err) {
      announce(`That didn’t save: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  }

  async function addLawText() {
    if (lawText.trim().length < MIN_LAW_TEXT) {
      setLawTextError(`${(MIN_LAW_TEXT - lawText.trim().length).toLocaleString()} more characters needed.`);
      return;
    }
    setBusy(true);
    setLawTextError(null);
    try {
      await actOnAuthoringItem(itemId, 'add_statute_text', { text: lawText });
      await refresh();
      announce('Law text saved. A draft also runs from it; if it files a rule, this law leaves the list.');
    } catch (err) {
      setLawTextError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  const vocabRequest = (item.detail as { vocabulary_request?: { concept?: string } }).vocabulary_request?.concept;
  const missing = Array.isArray((item.detail as { missing_concepts?: unknown }).missing_concepts)
    ? ((item.detail as { missing_concepts: unknown[] }).missing_concepts.filter((c) => typeof c === 'string') as string[])
    : [];
  const ruleNo = filed.length + 1;
  const source = ctx.law.source_url && /^https?:\/\//i.test(ctx.law.source_url) ? ctx.law.source_url : null;

  const pane = text ? (
    <LawTextPane
      text={text}
      originLabel={ctx.statute_text?.origin === 'editor_supplied' ? 'Added by an editor' : 'From the law source'}
      focusRange={focusRange}
      quoteTargets={quoteTargets}
      defaultTarget={lastQuoteField}
      onUseSelection={useSelection}
    />
  ) : (
    <section aria-labelledby="law-text-h" style={{ display: 'grid', gap: 'var(--space-2)' }}>
      <h2 id="law-text-h" tabIndex={-1} style={{ margin: 0, fontSize: 'var(--font-size-md)', color: 'var(--text)' }}>
        Law text
      </h2>
      <Field
        id="law-text-paste"
        label="Paste the law text to quote from"
        hint={`At least ${MIN_LAW_TEXT.toLocaleString()} characters (${lawText.trim().length.toLocaleString()} so far). It is saved with your name.`}
        error={lawTextError}
      >
        <Textarea rows={10} value={lawText} onChange={(e) => setLawText(e.target.value)} />
      </Field>
      <div>
        <Button compact variant="primary" onClick={() => void addLawText()} aria-disabled={busy || undefined}>
          Save law text
        </Button>
      </div>
    </section>
  );

  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
      <PageHeader
        variant="app"
        headingId="author-rule-h1"
        title={`Author the rule: ${law}`}
        subtitle={
          <span style={{ display: 'inline-flex', gap: 'var(--space-2)', flexWrap: 'wrap', alignItems: 'center' }}>
            <span>{jurisdictionName(ctx.law.jurisdiction)}</span>
            {ctx.law.effective_date ? <span>· Effective {fmtDate(ctx.law.effective_date)}</span> : null}
            <Badge>{item.state === 'blocked_vocabulary' ? 'Waiting for a rule field' : REASON_LABEL[item.reason_code]}</Badge>
            {source ? (
              <a href={source} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>
                Law source
              </a>
            ) : null}
          </span>
        }
        right={
          <Button compact variant="secondary" onClick={onBack}>
            Back to Needs authoring
          </Button>
        }
      />

      {/* One persistent status region. */}
      <div role="status" aria-live="polite">
        {message ? (
          <p key={message.n} style={{ margin: 0, color: 'var(--text)', fontSize: 'var(--font-size-2xs)' }}>
            {message.text}
          </p>
        ) : null}
      </div>

      {restored ? (
        <p style={{ margin: 0, display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap', fontSize: 'var(--font-size-2xs)', color: 'var(--text-muted)' }}>
          Your draft from this device is back.
          <Button
            compact
            variant="ghost"
            onClick={() => {
              save(draftKey, null);
              setForm(initialState(ctx));
              setRestored(false);
              announce('Started again from the law.');
            }}
          >
            Start over
          </Button>
        </p>
      ) : null}

      {problems.length || serverIssues.length ? (
        <div
          id="problems-summary"
          ref={summaryRef}
          tabIndex={-1}
          aria-labelledby="problems-h"
          style={{ border: '1px solid var(--critical)', borderRadius: 'var(--radius-card)', background: 'var(--danger-bg)', padding: 'var(--space-3)' }}
        >
          <h2 id="problems-h" style={{ margin: 0, fontSize: 'var(--font-size-md)', color: 'var(--danger-fg)' }}>
            {problems.length ? `${problems.length} thing${problems.length === 1 ? '' : 's'} to fix before filing` : 'The rule wasn’t filed'}
          </h2>
          <ul style={{ margin: 'var(--space-2) 0 0', paddingLeft: 'var(--space-4)', color: 'var(--text)', fontSize: 'var(--font-size-2xs)' }}>
            {problems.map((p, i) => (
              <li key={`${p.target}-${i}`}>
                <a
                  href={`#${p.target}`}
                  style={{ color: 'var(--danger-fg)' }}
                  onClick={(e) => {
                    e.preventDefault();
                    const el = document.getElementById(p.target);
                    el?.scrollIntoView?.({ block: 'center' });
                    el?.focus();
                  }}
                >
                  {p.text}
                </a>
              </li>
            ))}
            {serverIssues.map((t) => (
              <li key={t}>{t}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <div
        style={{
          display: 'grid',
          gap: 'var(--space-4)',
          gridTemplateColumns: narrow ? 'minmax(0, 1fr)' : 'minmax(0, 3fr) minmax(0, 2fr)',
          alignItems: 'start',
        }}
      >
        <form
          aria-labelledby="rule-form-h"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            // A several-rule law keeps going by default; a single-rule law files and finishes.
            void submit(!multi);
          }}
          style={{ display: 'grid', gap: 'var(--space-3)', minWidth: 0 }}
        >
          <h2 id="rule-form-h" ref={formHeadingRef} tabIndex={-1} style={{ margin: 0, fontSize: 'var(--font-size-md)', color: 'var(--text)' }}>
            {multi ? `Rule ${ruleNo}` : 'The rule'}
            {filed.length ? (
              <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: 'var(--font-size-2xs)' }}>
                {' '}· {filed.length} filed for review
              </span>
            ) : null}
          </h2>
          {filed.length ? (
            <ul style={{ margin: 0, paddingLeft: 'var(--space-4)', color: 'var(--text-muted)', fontSize: 'var(--font-size-2xs)' }}>
              {filed.map((f, i) => (
                <li key={f.change_id}>
                  Rule {i + 1}: {f.obligation_titles.slice(0, 2).join('; ')}
                  {f.obligation_titles.length > 2 ? ` (+${f.obligation_titles.length - 2})` : ''} ·{' '}
                  {f.status === 'pending' ? 'waiting for review' : f.status ?? 'filed'}
                </li>
              ))}
            </ul>
          ) : null}
          {narrow && text ? (
            <div>
              <Button type="button" compact variant="secondary" onClick={() => setSheetOpen(true)}>
                Law text
              </Button>
            </div>
          ) : null}

          <Card title="Who it applies to">
            <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
              {item.reason_code === 'vocabulary_gap' && (missing.length || vocabRequest) ? (
                <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap', fontSize: 'var(--font-size-2xs)', color: 'var(--text)' }}>
                  {vocabRequest ? (
                    <span>Rule field requested: {vocabRequest.replace(/_/g, ' ')}.</span>
                  ) : (
                    <>
                      <span>Rules can’t describe yet: {missing.map((m) => m.replace(/_/g, ' ')).join(', ')}.</span>
                      <Button type="button" compact variant="ghost" onClick={() => void requestField(toDutyId(missing[0]!))}>
                        Request this rule field
                      </Button>
                    </>
                  )}
                </div>
              ) : null}
              <fieldset style={{ border: 0, padding: 0, margin: 0, display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                <legend style={{ fontSize: 'var(--font-size-2xs)', fontWeight: 500, color: 'var(--text)', marginBottom: 'var(--space-1)' }}>Match</legend>
                <Radio name="combinator" label="All conditions" checked={form.combinator === 'all'} onChange={() => set({ combinator: 'all' })} />
                <Radio name="combinator" label="Any condition" checked={form.combinator === 'any'} onChange={() => set({ combinator: 'any' })} />
              </fieldset>
              <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 'var(--space-2)' }}>
                {form.conditions.map((c, i) => (
                  <ConditionRow
                    key={i}
                    index={i}
                    condition={c}
                    join={i === 0 ? null : form.combinator === 'all' ? 'and' : 'or'}
                    canRemove={form.conditions.length > 1}
                    onChange={(next) => set({ conditions: form.conditions.map((x, j) => (j === i ? next : x)) })}
                    onRemove={() => {
                      set({ conditions: form.conditions.filter((_, j) => j !== i) });
                      announce(`Condition ${i + 1} removed.`);
                      focusOn('add-condition');
                    }}
                  />
                ))}
              </ol>
              <div>
                <Button
                  id="add-condition"
                  type="button"
                  compact
                  variant="ghost"
                  onClick={() => {
                    const n = form.conditions.length;
                    set({ conditions: [...form.conditions, blankCondition()] });
                    focusOn(`cond-${n}-field`);
                  }}
                >
                  Add a condition
                </Button>
              </div>
              {form.triggerQuotes.map((q, i) => (
                <QuoteField
                  key={i}
                  id={`tq-${i}`}
                  label={form.triggerQuotes.length > 1 ? `Quote showing who the law covers (${i + 1})` : 'Quote showing who the law covers'}
                  value={q}
                  status={status(q)}
                  hasText={!!text}
                  onChange={(v) => set({ triggerQuotes: form.triggerQuotes.map((x, j) => (j === i ? v : x)) })}
                  findName={form.triggerQuotes.length > 1 ? `who it applies to, quote ${i + 1}` : 'who it applies to quote'}
                  onUse={() => setLastQuoteField(`tq-${i}`)}
                  onFind={() => find(form.triggerQuotes.length > 1 ? `Who it applies to, quote ${i + 1}` : 'Who it applies to', q)}
                  onBlurCheck={(s) => s !== 'empty' && announce(`Who it applies to: ${quoteMessage(s, q)}`)}
                  onRemove={
                    form.triggerQuotes.length > 1
                      ? () => {
                          set({ triggerQuotes: form.triggerQuotes.filter((_, j) => j !== i) });
                          focusOn('tq-0');
                        }
                      : undefined
                  }
                />
              ))}
              {form.triggerQuotes.length < MAX_QUOTES ? (
                <div>
                  <Button
                    type="button"
                    compact
                    variant="ghost"
                    onClick={() => {
                      const n = form.triggerQuotes.length;
                      set({ triggerQuotes: [...form.triggerQuotes, ''] });
                      focusOn(`tq-${n}`);
                    }}
                  >
                    Add another quote
                  </Button>
                </div>
              ) : null}
            </div>
          </Card>

          {form.duties.map((d, i) => (
            <DutyCard
              key={d.key}
              index={i}
              duty={d}
              terms={terms}
              itemId={itemId}
              dutyState={dutyState}
              knownDuties={knownDuties}
              controls={ctx.controls ?? []}
              proposedIn={proposedIn(i)}
              showErrors={problems.length > 0}
              announce={announce}
              focusOn={focusOn}
              pendingDuties={ctx.duties?.pending ?? []}
              jurisdictionLabel={jurisdictionName(ctx.law.jurisdiction)}
              sourceUrl={source}
              onTermFiled={async (term) => {
                setDuty(d.key, term.kind === 'role' ? { roleTerms: [...d.roleTerms, term.term_key], scope: [] } : { tierTerm: term.term_key, tier: '' });
                await refresh();
                announce(`“${term.label}” filed for review and picked for Duty ${i + 1}. The rule applies only after the word is approved.`);
              }}
              status={status(d.quote)}
              hasText={!!text}
              canRemove={form.duties.length > 1}
              onChange={(patch) => setDuty(d.key, patch)}
              onUse={() => setLastQuoteField(`dq-${d.key}`)}
              onFind={() => find(`Duty ${i + 1}`, d.quote)}
              onBlurCheck={(s) => s !== 'empty' && announce(`Duty ${i + 1}: ${quoteMessage(s, d.quote)}`)}
              onRemove={() => {
                set({ duties: form.duties.filter((x) => x.key !== d.key) });
                announce(`Duty ${i + 1} removed.`);
                focusOn('add-duty');
              }}
            />
          ))}
          {form.duties.length < MAX_DUTIES ? (
            <div>
              <Button
                id="add-duty"
                type="button"
                compact
                variant="ghost"
                onClick={() => {
                  const fresh = blankDuty(ctx.law.bill_id ?? '');
                  set({ duties: [...form.duties, fresh] });
                  focusOn(`dt-${fresh.key}`);
                }}
              >
                Add a duty
              </Button>
            </div>
          ) : null}

          <Card>
            <Field
              id="starts-on"
              label="Starts on"
              hint="The earliest date. A duty can start later."
              required
            >
              <Input type="date" value={form.effectiveDate} onChange={(e) => set({ effectiveDate: e.target.value })} />
            </Field>
          </Card>

          <Card title="Test cases">
            <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
              <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 'var(--font-size-2xs)' }}>
                {form.cases === null
                  ? 'Starting cases. They update as you change the conditions until you edit one.'
                  : 'Each fact is one thing about the system. A list such as What the AI does can have several.'}
              </p>
              {cases.length === 0 ? (
                <p style={{ margin: 0, color: 'var(--text)', fontSize: 'var(--font-size-2xs)' }}>
                  No cases yet. Add a condition under Who it applies to and two starting cases appear, or add your own.
                </p>
              ) : null}
              <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 'var(--space-2)' }}>
                {cases.map((c, i) => (
                  <CaseCard
                    key={c.key}
                    index={i}
                    value={c}
                    result={caseResults[i]!}
                    onChange={(patch) => updateCases((cur) => cur.map((x) => (x.key === c.key ? { ...x, ...patch } : x)))}
                    onFact={(factKey, patch) =>
                      updateCases((cur) => cur.map((x) => (x.key === c.key ? { ...x, facts: x.facts.map((f) => (f.key === factKey ? { ...f, ...patch } : f)) } : x)))
                    }
                    onAddFact={() => {
                      const key = nextFactKey(cases);
                      updateCases((cur) => cur.map((x) => (x.key === c.key ? { ...x, facts: [...x.facts, blankFact(key)] } : x)));
                      focusOn(`cf-${key}-field`);
                    }}
                    onRemoveFact={(factKey) => {
                      updateCases((cur) => cur.map((x) => (x.key === c.key ? { ...x, facts: x.facts.filter((f) => f.key !== factKey) } : x)));
                      announce('Fact removed.');
                      focusOn(`cadd-${c.key}`);
                    }}
                    onRemove={() => {
                      setUndoCases({ cases: form.cases, text: `Test case ${i + 1} removed.` });
                      updateCases((cur) => cur.filter((x) => x.key !== c.key));
                      announce(`Test case ${i + 1} removed. Undo is next to Add a test case.`);
                      focusOn('add-case');
                    }}
                  />
                ))}
              </ol>
              <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', alignItems: 'center' }}>
                <Button
                  id="add-case"
                  type="button"
                  compact
                  variant="ghost"
                  onClick={() => {
                    // Offer the kind of case still missing first: one that applies, then one that does not.
                    const fresh = blankCase(cases, !cases.some((c) => c.expected));
                    updateCases((cur) => [...cur, fresh]);
                    announce(`Test case ${cases.length + 1} added.`);
                    focusOn(`cn-${fresh.key}`);
                  }}
                >
                  Add a test case
                </Button>
                {differsFromStarting ? (
                  <Button
                    type="button"
                    compact
                    variant="ghost"
                    onClick={() => {
                      setUndoCases({ cases: form.cases, text: 'Test cases replaced.' });
                      set({ cases: null });
                      announce('Test cases replaced with the starting cases built from your conditions. Undo is next to Add a test case.');
                      focusOn('add-case');
                    }}
                  >
                    Replace with starting cases
                  </Button>
                ) : null}
                {undoCases ? (
                  <Button
                    type="button"
                    compact
                    variant="secondary"
                    onClick={() => {
                      set({ cases: undoCases.cases });
                      setUndoCases(null);
                      announce('Undone.');
                    }}
                  >
                    Undo: {undoCases.text.replace(/\.$/, '').toLowerCase()}
                  </Button>
                ) : null}
                {badCases.length ? (
                  <a
                    id="cases-to-fix"
                    href={`#${badCases[0]!.id}`}
                    style={{ color: 'var(--danger-fg)', fontSize: 'var(--font-size-2xs)' }}
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById(badCases[0]!.id)?.focus();
                    }}
                  >
                    {badCases.length} test case{badCases.length === 1 ? '' : 's'} to fix
                  </a>
                ) : missingKinds.length ? (
                  <Button
                    type="button"
                    compact
                    variant="secondary"
                    onClick={() => {
                      const fresh = blankCase(cases, missingKinds[0]!.includes('should apply'));
                      updateCases((cur) => [...cur, fresh]);
                      announce(`Test case ${cases.length + 1} added.`);
                      focusOn(`cn-${fresh.key}`);
                    }}
                  >
                    {missingKinds[0]!.includes('should apply') ? 'Add a case the rule should apply to' : 'Add a case the rule should not apply to'}
                  </Button>
                ) : null}
              </div>
            </div>
          </Card>

          {usesPendingTerm ? (
            <p id="pending-term-note" style={{ margin: 0, color: 'var(--text-muted)', fontSize: 'var(--font-size-2xs)' }}>
              This rule uses a word that is waiting for review. It applies only after the word is approved.
            </p>
          ) : null}

          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', alignItems: 'center' }}>
            {failing.length || badCases.length || missingKinds.length ? (
              <a
                id="quotes-to-fix"
                href={`#${(failing[0] ?? badCases[0] ?? { id: 'add-case' }).id}`}
                style={{ color: 'var(--danger-fg)', fontSize: 'var(--font-size-2xs)' }}
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById((failing[0] ?? badCases[0] ?? { id: 'add-case' }).id)?.focus();
                }}
              >
                {[
                  failing.length ? `${failing.length} quote${failing.length === 1 ? '' : 's'}` : '',
                  badCases.length ? `${badCases.length} test case${badCases.length === 1 ? '' : 's'}` : '',
                  !badCases.length && missingKinds.length ? 'a test case' : '',
                ]
                  .filter(Boolean)
                  .join(' and ')}{' '}
                to fix
              </a>
            ) : null}
            {multi ? (
              <>
                <Button
                  type="submit"
                  variant={failing.length || badCases.length || missingKinds.length ? 'secondary' : 'primary'}
                  aria-disabled={busy || undefined}
                  aria-describedby={failing.length || badCases.length || missingKinds.length ? 'quotes-to-fix' : undefined}
                >
                  {busy ? 'Filing…' : `File rule ${ruleNo} and write the next`}
                </Button>
                <Button type="button" variant="secondary" aria-disabled={busy || undefined} onClick={() => void submit(true)}>
                  {filed.length ? `File rule ${ruleNo} and finish` : 'File as the only rule'}
                </Button>
              </>
            ) : (
              <Button
                type="submit"
                variant={failing.length || badCases.length || missingKinds.length ? 'secondary' : 'primary'}
                aria-disabled={busy || undefined}
                aria-describedby={failing.length || badCases.length || missingKinds.length ? 'quotes-to-fix' : undefined}
              >
                {busy ? 'Filing…' : 'File for review'}
              </Button>
            )}
            {multi && filed.length ? (
              <Button type="button" variant="ghost" aria-disabled={busy || undefined} onClick={() => void finishWithoutAnother()}>
                Finish with {filed.length} rule{filed.length === 1 ? '' : 's'}
              </Button>
            ) : null}
            <Button type="button" variant="ghost" onClick={onBack}>
              Cancel
            </Button>
          </div>
        </form>

        {narrow ? (
          text ? (
            <Drawer open={sheetOpen} onClose={() => setSheetOpen(false)} label="Law text" modal>
              <div style={{ display: 'grid', gap: 'var(--space-2)', padding: 'var(--space-3)' }}>
                <div>
                  <Button compact variant="secondary" onClick={() => setSheetOpen(false)}>
                    Back to the form
                  </Button>
                </div>
                {pane}
              </div>
            </Drawer>
          ) : (
            pane
          )
        ) : (
          <aside aria-label="Law text" style={{ position: 'sticky', top: 'var(--space-3)', minWidth: 0 }}>
            {pane}
          </aside>
        )}
      </div>
    </div>
  );
}

function ConditionRow({ index, condition, join, canRemove, onChange, onRemove }: {
  index: number;
  condition: Condition;
  join: 'and' | 'or' | null;
  canRemove: boolean;
  onChange: (c: Condition) => void;
  onRemove: () => void;
}) {
  const spec = fieldSpec(condition.field);
  const kind = spec?.kind ?? 'array';
  const ops = operatorsFor(kind);
  const id = `cond-${index}`;
  return (
    <li>
      <fieldset style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-card)', padding: 'var(--space-2) var(--space-3)', margin: 0 }}>
        <legend style={{ fontSize: 'var(--font-size-2xs)', color: 'var(--text-muted)', padding: '0 var(--space-1)' }}>
          {join ? `${join === 'and' ? 'And' : 'Or'} · ` : ''}Condition {index + 1}
        </legend>
        <div style={{ display: 'grid', gap: 'var(--space-2)', gridTemplateColumns: 'repeat(auto-fit, minmax(10rem, 1fr))', alignItems: 'end' }}>
          <Field id={`${id}-field`} label="Rule field">
            <Select value={condition.field} onChange={(e) => onChange(blankCondition(e.target.value))}>
              {FIELD_CATALOG.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label}
                </option>
              ))}
            </Select>
          </Field>
          {ops.length > 1 ? (
            <Field id={`${id}-op`} label="Test">
              <Select value={condition.op} onChange={(e) => onChange({ ...condition, op: e.target.value as Condition['op'] })}>
                {ops.map((o) => (
                  <option key={o} value={o}>
                    {OPERATOR_LABEL[o]}
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}
          {condition.op === 'has_any' ? null : kind === 'boolean' ? (
            <Field id={`${id}-value`} label="Value">
              <Select value={condition.value} onChange={(e) => onChange({ ...condition, value: e.target.value })}>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </Select>
            </Field>
          ) : spec?.options ? (
            <Field id={`${id}-value`} label="Value">
              <Select value={condition.value} onChange={(e) => onChange({ ...condition, value: e.target.value })}>
                {spec.options.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </Field>
          ) : (
            <Field id={`${id}-value`} label="Value">
              <Input value={condition.value} onChange={(e) => onChange({ ...condition, value: e.target.value })} />
            </Field>
          )}
          {canRemove ? (
            <div>
              <Button type="button" compact variant="ghost" onClick={onRemove} aria-label={`Remove condition ${index + 1}`}>
                Remove
              </Button>
            </div>
          ) : null}
        </div>
      </fieldset>
    </li>
  );
}

function QuoteField({ id, label, findName, value, status, hasText, onChange, onFind, onUse, onBlurCheck, onRemove }: {
  id: string;
  label: string;
  /** Names the Find button in context, visible text first ("Find in law text: Duty 2 quote"). */
  findName: string;
  onUse: () => void;
  value: string;
  status: QuoteStatus;
  hasText: boolean;
  onChange: (v: string) => void;
  onFind: () => void;
  onBlurCheck: (s: QuoteStatus) => void;
  onRemove?: () => void;
}) {
  const failing = status === 'too_short' || status === 'not_in_text';
  return (
    <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
      <Field
        id={id}
        label={label}
        hint={failing ? undefined : status === 'found' ? '✓ Found in the law text.' : 'Copy it word for word from the law text.'}
        error={failing ? quoteMessage(status, value) : null}
        errorRole="none"
        required
      >
        <Textarea rows={3} value={value} onFocus={onUse} onChange={(e) => onChange(e.target.value)} onBlur={() => onBlurCheck(status)} />
      </Field>
      <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
        {hasText ? (
          <Button
            type="button"
            compact
            variant="secondary"
            onClick={() => {
              onUse();
              onFind();
            }}
            aria-label={`Find in law text: ${findName}`}
          >
            Find in law text
          </Button>
        ) : null}
        {onRemove ? (
          <Button type="button" compact variant="ghost" onClick={onRemove} aria-label={`Remove ${label}`}>
            Remove quote
          </Button>
        ) : null}
      </div>
    </div>
  );
}

/** Select value prefix for a plain tier kept while the law has tier words. */
const PLAIN = 'plain:';

/** "C.R.S. 6-1-1701 · Waiting for review": a term's citation, and whether it is approved yet. */
function choiceNote(c: LabelChoice): string {
  return [c.citation, c.pending ? 'Waiting for review' : ''].filter(Boolean).join(' · ');
}

/** ADR-0077 §6: a term's definition sits behind a disclosure, not inline. */
function Definition({ text }: { text: string }) {
  return (
    <details style={{ fontSize: 'var(--font-size-2xs)', color: 'var(--text-muted)' }}>
      <summary style={{ cursor: 'pointer', color: 'var(--text)' }}>Definition</summary>
      <p style={{ margin: 'var(--space-1) 0 0', whiteSpace: 'pre-wrap' }}>{text}</p>
    </details>
  );
}

function DutyCard({ index, duty, terms, itemId, dutyState, knownDuties, pendingDuties, controls, proposedIn, showErrors, announce, focusOn, jurisdictionLabel, sourceUrl, onTermFiled, status, hasText, canRemove, onChange, onFind, onUse, onBlurCheck, onRemove }: {
  /** V104-G-12: the law's jurisdiction's role and tier words (active and pending). */
  terms: readonly JurisdictionTerm[];
  /** V112-5-5: whether a short name is a known duty, waiting for review, or new. */
  dutyState: (id: string) => 'known' | 'pending' | 'new';
  knownDuties: readonly string[];
  controls: ReadonlyArray<{ control_id: string; title: string }>;
  /** Round 1 #6: an earlier card already proposes this short name as a new duty. */
  proposedIn: { index: number; key: number } | null;
  showErrors: boolean;
  announce: (text: string) => void;
  /** Deferred focus (after the render that adds or removes the target). */
  focusOn: (id: string) => void;
  /** Duties waiting for review as new terms: also offered by "Did you mean". */
  pendingDuties: readonly string[];
  itemId: string;
  jurisdictionLabel: string;
  sourceUrl: string | null;
  onTermFiled: (term: JurisdictionTerm) => void | Promise<void>;
  onUse: () => void;
  index: number;
  duty: DutyDraft;
  status: QuoteStatus;
  hasText: boolean;
  canRemove: boolean;
  onChange: (patch: Partial<DutyDraft>) => void;
  onFind: () => void;
  onBlurCheck: (s: QuoteStatus) => void;
  onRemove: () => void;
}) {
  const k = duty.key;
  const n = `Duty ${index + 1}`;
  // V104-G-12 / ADR-0077 §6: the jurisdiction's own words, or plain labels when it declares none.
  const roles = roleChoices(terms);
  const tiers = tierChoices(terms);
  // A plain pick made before the law had words stays visible (and filed) until a law word replaces it.
  const plainTier = tiers.mode === 'terms' && !duty.tierTerm && duty.tier ? duty.tier : null;
  const plainRoles = roles.mode === 'terms' && duty.roleTerms.length === 0 ? duty.scope : [];
  const tierValue = tiers.mode === 'terms' ? (plainTier ? `${PLAIN}${plainTier}` : duty.tierTerm) : duty.tier;
  const tierPicked = tiers.choices.find((c) => c.value === tierValue);
  const [adding, setAdding] = useState<'role' | 'tier' | null>(null);
  const dutyId = toDutyId(duty.duty);
  const nameState = dutyId.length >= 2 ? dutyState(dutyId) : 'known';
  const open = nameState === 'new' && !proposedIn && proposing(duty);
  // Round 2: the "Did you mean / Propose" row waits until the short name is left, so it doesn't flicker while typing.
  const [typing, setTyping] = useState(false);
  const suggestion = nameState === 'new' && !proposedIn && !open ? closestDuty(dutyId, [...knownDuties, ...pendingDuties]) : null;
  const nameHint =
    nameState === 'pending'
      ? 'Waiting for review as a new duty; this rule is approved with it.'
      : nameState === 'new'
        ? proposedIn
          ? `Proposed as a new duty in Duty ${proposedIn.index + 1}.`
          : open
            ? 'A new duty: describe it below.'
            : `“${duty.duty.trim()}” isn’t a known duty.`
        : 'Reused across laws for the same kind of duty.';
  return (
    <Card
      title={n}
      right={
        canRemove ? (
          <Button type="button" compact variant="ghost" onClick={onRemove} aria-label={`Remove ${n}`}>
            Remove
          </Button>
        ) : null
      }
    >
      <section aria-label={n} style={{ display: 'grid', gap: 'var(--space-3)' }}>
        <Field id={`dt-${k}`} label="Title" hint="What the duty asks, e.g. Remove reported images within 48 hours." required>
          <Input
            value={duty.title}
            onChange={(e) => {
              const title = e.target.value;
              onChange(duty.autoName ? { title, duty: title } : { title });
            }}
          />
        </Field>
        <div style={{ display: 'grid', gap: 'var(--space-2)', gridTemplateColumns: 'repeat(auto-fit, minmax(12rem, 1fr))' }}>
          <Field id={`dn-${k}`} label="Short name" hint={nameHint} required>
            <Input
              value={duty.duty}
              list={`duties-${k}`}
              autoComplete="off"
              onChange={(e) => { setTyping(true); onChange({ duty: e.target.value, autoName: false }); }}
              onBlur={() => setTyping(false)}
            />
          </Field>
          <Field id={`dc-${k}`} label="Section" required>
            <Input value={duty.citation} onChange={(e) => onChange({ citation: e.target.value })} />
          </Field>
        </div>
        <datalist id={`duties-${k}`}>
          {knownDuties.map((d) => (
            <option key={d} value={d.replace(/_/g, ' ')} />
          ))}
        </datalist>
        {nameState === 'new' && proposedIn ? (
          <div>
            <Button type="button" compact variant="ghost" onClick={() => focusOn(`ndl-${proposedIn.key}`)}>
              {`Go to Duty ${proposedIn.index + 1}`}
            </Button>
          </div>
        ) : open ? (
          <NewDutyFields
            dutyKey={k}
            dutyLabel={n}
            titleHint={duty.title.trim()}
            value={duty.newDuty!}
            controls={controls}
            showErrors={showErrors}
            onChange={(patch) => onChange({ newDuty: { ...duty.newDuty!, ...patch } })}
            onClose={() => {
              onChange({ newDuty: { ...duty.newDuty!, open: false } });
              announce(`${n}: new duty fields closed. What you typed is kept.`);
              focusOn(`dnp-${k}`);
            }}
          />
        ) : nameState === 'new' && !typing ? (
          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
            {suggestion ? (
              <>
                <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-3xs)' }}>{`Did you mean “${suggestion.replace(/_/g, ' ')}”?`}</span>
                <Button
                  type="button"
                  compact
                  variant="secondary"
                  onClick={() => {
                    onChange({ duty: suggestion.replace(/_/g, ' '), autoName: false });
                    announce(`${n}: short name set to ${suggestion.replace(/_/g, ' ')}.`);
                    focusOn(`dn-${k}`);
                  }}
                >
                  Use it
                </Button>
              </>
            ) : null}
            <Button
              id={`dnp-${k}`}
              type="button"
              compact
              variant={suggestion ? 'ghost' : 'secondary'}
              onClick={() => {
                // The short name stops following the title once it is proposed (round 1 #2).
                onChange({ newDuty: duty.newDuty ? { ...duty.newDuty, open: true } : blankNewDuty(duty.title, controls.length > 0), autoName: false });
                announce(`${n}: new duty fields added.`);
                focusOn(`ndl-${k}`);
              }}
            >
              Propose as new duty
            </Button>
          </div>
        ) : null}
        <div style={{ display: 'grid', gap: 'var(--space-2)', gridTemplateColumns: 'repeat(auto-fit, minmax(10rem, 1fr))', alignItems: 'end' }}>
          <Field id={`dk-${k}`} label="Deadline">
            <Select value={duty.deadline} onChange={(e) => onChange({ deadline: e.target.value as DeadlineKind })}>
              {(Object.keys(DEADLINE_LABEL) as DeadlineKind[]).map((d) => (
                <option key={d} value={d}>
                  {DEADLINE_LABEL[d]}
                </option>
              ))}
            </Select>
          </Field>
          {duty.deadline !== 'none' ? (
            <>
              <Field id={`da-${k}`} label={AMOUNT_LABEL[duty.deadline]} required>
                <Input type="number" min={1} step={1} inputMode="numeric" value={duty.amount} onChange={(e) => onChange({ amount: e.target.value })} />
              </Field>
              <Field id={`db-${k}`} label="Counted from">
                <Input value={duty.basis} onChange={(e) => onChange({ basis: e.target.value })} />
              </Field>
            </>
          ) : null}
        </div>
        <Field id={`de-${k}`} label="Starts on (optional)" hint="Empty = starts with the rule.">
          <Input type="date" value={duty.startsOn} onChange={(e) => onChange({ startsOn: e.target.value })} />
        </Field>
        <div style={{ display: 'grid', gap: 'var(--space-2)', gridTemplateColumns: 'repeat(auto-fit, minmax(12rem, 1fr))', alignItems: 'start' }}>
          <div style={{ display: 'grid', gap: 'var(--space-1)', alignContent: 'start' }}>
            <Field id={`dr-${k}`} label="Risk tier (optional)" hint={tierPicked ? choiceNote(tierPicked) : undefined}>
              <Select
                value={tierValue}
                onChange={(e) => {
                  const v = e.target.value;
                  if (tiers.mode === 'plain') onChange({ tier: v as DutyDraft['tier'] });
                  else if (v.startsWith(PLAIN)) onChange({ tier: v.slice(PLAIN.length) as DutyDraft['tier'], tierTerm: '' });
                  else onChange({ tierTerm: v, tier: '' });
                }}
              >
                <option value="">None</option>
                {tiers.choices.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                    {c.pending ? ' (waiting for review)' : ''}
                  </option>
                ))}
                {plainTier ? <option value={`${PLAIN}${plainTier}`}>{`${TIER_LABEL[plainTier]} (no ${jurisdictionLabel} word yet)`}</option> : null}
              </Select>
            </Field>
            {tierPicked?.definition ? <Definition text={tierPicked.definition} /> : null}
            <div>
              <Button
                id={`addterm-tier-${k}`}
                type="button"
                compact
                variant="ghost"
                aria-expanded={adding === 'tier'}
                aria-label={`Add a term: risk tier, ${n}`}
                onClick={() => setAdding('tier')}
              >
                Add a term
              </Button>
            </div>
          </div>
          <fieldset style={{ border: 0, padding: 0, margin: 0, display: 'grid', gap: 'var(--space-1)' }}>
            <legend style={{ fontSize: 'var(--font-size-2xs)', fontWeight: 500, color: 'var(--text)', marginBottom: 'var(--space-1)' }}>
              Only for (optional)
            </legend>
            {roles.choices.map((c) => {
              const picked = roles.mode === 'terms' ? duty.roleTerms : (duty.scope as string[]);
              return (
                <div key={c.value} style={{ display: 'grid', gap: 'var(--space-1)' }}>
                  <Checkbox
                    id={`dro-${k}-${c.value}`}
                    label={c.label}
                    description={c.citation || c.pending ? choiceNote(c) : undefined}
                    checked={picked.includes(c.value)}
                    onChange={(e) => {
                      const on = e.currentTarget.checked;
                      const next = on ? [...picked, c.value] : picked.filter((x) => x !== c.value);
                      // A law word replaces any plain pick: a duty names its roles one way.
                      onChange(roles.mode === 'terms' ? { roleTerms: next, scope: [] } : { scope: next as DutyDraft['scope'] });
                    }}
                  />
                  {c.definition ? <Definition text={c.definition} /> : null}
                </div>
              );
            })}
            {plainRoles.map((r) => (
              <Checkbox
                key={`plain-${r}`}
                id={`dro-${k}-plain-${r}`}
                label={`${CANONICAL_ROLE_LABEL[r]} (no ${jurisdictionLabel} word yet)`}
                checked
                onChange={() => onChange({ scope: duty.scope.filter((x) => x !== r) })}
              />
            ))}
            <div>
              <Button
                id={`addterm-role-${k}`}
                type="button"
                compact
                variant="ghost"
                aria-expanded={adding === 'role'}
                aria-label={`Add a term: role, ${n}`}
                onClick={() => setAdding('role')}
              >
                Add a term
              </Button>
            </div>
          </fieldset>
        </div>
        {adding ? (
          <TermProposalForm
            key={adding}
            itemId={itemId}
            jurisdictionLabel={jurisdictionLabel}
            kind={adding}
            defaultSourceUrl={sourceUrl}
            defaultCitation={duty.citation}
            existing={terms}
            onCancel={() => {
              const back = `addterm-${adding}-${k}`;
              setAdding(null);
              requestAnimationFrame(() => document.getElementById(back)?.focus());
            }}
            onUseExisting={(term) => {
              setAdding(null);
              if (term.kind === 'role') onChange({ roleTerms: [...new Set([...duty.roleTerms, term.term_key])], scope: [] });
              else onChange({ tierTerm: term.term_key, tier: '' });
              requestAnimationFrame(() => document.getElementById(term.kind === 'role' ? `dro-${k}-${term.term_key}` : `dr-${k}`)?.focus());
            }}
            onFiled={(term) => {
              setAdding(null);
              void Promise.resolve(onTermFiled(term)).finally(() =>
                requestAnimationFrame(() => document.getElementById(term.kind === 'role' ? `dro-${k}-${term.term_key}` : `dr-${k}`)?.focus())
              );
            }}
          />
        ) : null}
        <QuoteField
          id={`dq-${k}`}
          label="Quote that imposes this duty"
          findName={`${n} quote`}
          onUse={onUse}
          value={duty.quote}
          status={status}
          hasText={hasText}
          onChange={(v) => onChange({ quote: v })}
          onFind={onFind}
          onBlurCheck={onBlurCheck}
        />
      </section>
    </Card>
  );
}

function CaseCard({ index, value, result, onChange, onFact, onAddFact, onRemoveFact, onRemove }: {
  index: number;
  value: CaseDraft;
  result: CaseResult;
  onChange: (patch: Partial<CaseDraft>) => void;
  onFact: (factKey: number, patch: Partial<Fact>) => void;
  onAddFact: () => void;
  onRemoveFact: (factKey: number) => void;
  onRemove: () => void;
}) {
  const n = `Test case ${index + 1}`;
  const bad = result.state === 'disagrees' || result.state === 'error';
  const msgId = `cm-${value.key}`;
  return (
    <li>
      <fieldset
        aria-describedby={msgId}
        style={{
          border: `1px solid ${bad ? 'var(--critical)' : 'var(--border)'}`,
          borderRadius: 'var(--radius-card)',
          padding: 'var(--space-2) var(--space-3)',
          margin: 0,
          display: 'grid',
          gap: 'var(--space-2)',
        }}
      >
        <legend style={{ fontSize: 'var(--font-size-2xs)', color: 'var(--text-muted)', padding: '0 var(--space-1)' }}>{n}</legend>
        <div style={{ display: 'grid', gap: 'var(--space-2)', gridTemplateColumns: 'repeat(auto-fit, minmax(12rem, 1fr))', alignItems: 'start' }}>
          <Field id={`cn-${value.key}`} label="Name (optional)">
            <Input value={value.label} onChange={(e) => onChange({ label: e.target.value })} />
          </Field>
          <fieldset style={{ border: 0, padding: 0, margin: 0, display: 'grid', gap: 'var(--space-1)' }}>
            <legend style={{ fontSize: 'var(--font-size-2xs)', fontWeight: 500, color: 'var(--text)', marginBottom: 'var(--space-1)' }}>The rule should</legend>
            <Radio name={`ce-${value.key}`} label="Apply to it" checked={value.expected} onChange={() => onChange({ expected: true })} />
            <Radio name={`ce-${value.key}`} label="Not apply to it" checked={!value.expected} onChange={() => onChange({ expected: false })} />
          </fieldset>
        </div>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 'var(--space-2)' }}>
          {value.facts.map((f, i) => (
            <FactRow
              key={f.key}
              index={i}
              caseNo={index + 1}
              fact={f}
              canRemove={value.facts.length > 1}
              onChange={(patch) => onFact(f.key, patch)}
              onRemove={() => onRemoveFact(f.key)}
            />
          ))}
        </ul>
        <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          <Button id={`cadd-${value.key}`} type="button" compact variant="ghost" onClick={onAddFact} aria-label={`Add a fact to test case ${index + 1}`}>
            Add a fact
          </Button>
          <Button type="button" compact variant="ghost" onClick={onRemove} aria-label={`Remove case: test case ${index + 1}`}>
            Remove case
          </Button>
        </div>
        <p id={msgId} role="status" style={{ margin: 0, fontSize: 'var(--font-size-2xs)', color: bad ? 'var(--danger-fg)' : 'var(--text-muted)' }}>
          <span aria-hidden="true">{bad ? '✕ ' : '✓ '}</span>
          {caseMessage(result, value)}
        </p>
      </fieldset>
    </li>
  );
}

function FactRow({ index, caseNo, fact, canRemove, onChange, onRemove }: {
  index: number;
  caseNo: number;
  fact: Fact;
  canRemove: boolean;
  onChange: (patch: Partial<Fact>) => void;
  onRemove: () => void;
}) {
  const spec = fieldSpec(fact.field);
  const kind = spec?.kind ?? 'array';
  const id = `cf-${fact.key}`;
  return (
    <li style={{ display: 'grid', gap: 'var(--space-2)', gridTemplateColumns: 'repeat(auto-fit, minmax(10rem, 1fr))', alignItems: 'end' }}>
      <Field id={`${id}-field`} label={`Fact ${index + 1}`}>
        <Select
          value={fact.field}
          onChange={(e) => {
            const next = blankFact(fact.key, e.target.value);
            onChange({ field: next.field, value: next.value });
          }}
        >
          {FIELD_CATALOG.map((f) => (
            <option key={f.key} value={f.key}>
              {f.label}
            </option>
          ))}
        </Select>
      </Field>
      {kind === 'boolean' ? (
        <Field id={`${id}-value`} label="Is it so?">
          <Select value={fact.value === 'false' ? 'false' : 'true'} onChange={(e) => onChange({ value: e.target.value })}>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </Select>
        </Field>
      ) : spec?.options ? (
        <Field id={`${id}-value`} label="Value">
          <Select value={fact.value} onChange={(e) => onChange({ value: e.target.value })}>
            {spec.options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
            <option value={ELSE_VALUE}>Any other value</option>
            {fact.value && fact.value !== ELSE_VALUE && !spec.options.some((o) => o.value === fact.value) ? (
              <option value={fact.value}>{fact.value.replace(/_/g, ' ')}</option>
            ) : null}
          </Select>
        </Field>
      ) : (
        <Field id={`${id}-value`} label="Value">
          <Input
            value={fact.value === ELSE_VALUE ? '' : fact.value}
            placeholder={fact.value === ELSE_VALUE ? 'Any other value' : undefined}
            onChange={(e) => onChange({ value: e.target.value })}
          />
        </Field>
      )}
      {canRemove ? (
        <div>
          <Button type="button" compact variant="ghost" onClick={onRemove} aria-label={`Remove fact ${index + 1} from test case ${caseNo}`}>
            Remove
          </Button>
        </div>
      ) : null}
    </li>
  );
}

function Done({ law, count, slug, itemId, onBack, headingRef, navigate }: {
  law: string;
  count: number;
  slug: string;
  itemId: string;
  onBack: () => void;
  headingRef: RefObject<HTMLHeadingElement>;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const list = useQuery({ queryKey: queryKeys.authoringItems(), queryFn: () => listAuthoringItems() });
  const next = (list.data?.items ?? []).find((i) => i.item_id !== itemId && i.actions.includes('author_rule'));
  const left = (list.data?.items ?? []).filter((i) => i.item_id !== itemId).length;
  return (
    <Card>
      <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
        <h1 ref={headingRef} tabIndex={-1} style={{ margin: 0, fontSize: 'var(--font-size-lg)', color: 'var(--text)' }}>
          Filed for review: {law}
        </h1>
        <p style={{ margin: 0, color: 'var(--text-muted)' }}>
          {count === 1 ? 'The rule is' : `${count} rules are`} in the Editorial queue.
        </p>
      </div>
      <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', marginTop: 'var(--space-3)' }}>
        {next ? (
          <Button variant="primary" onClick={() => navigate(`/w/${slug}/app/queue/authoring/${encodeURIComponent(next.item_id)}`)}>
            Author the next law ({left} left)
          </Button>
        ) : null}
        <Button variant={next ? 'secondary' : 'primary'} onClick={onBack}>
          Back to Needs authoring
        </Button>
      </div>
    </Card>
  );
}
